/**
 * Custom hook for fetching risky shipments data
 *
 * Returns high/critical risk containers sorted by monetary value.
 * Used in the Control Hub dashboard to highlight shipments requiring attention.
 */
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";
import type { ShipmentStatusFilter } from "@/types/api/containers-recap";

// ============================================================================
// TYPES
// ============================================================================

export interface RiskyShipment {
    container_id: string;
    container_number: string;
    origin_port_name: string | null;
    destination_port_name: string | null;
    origin_country_code: string | null;
    destination_country_code: string | null;
    risk_level: string;
    risk_score: number;
    has_active_alerts: boolean;
    alert_count: number;
    total_value_usd: number | null;
    freight_charges_usd: number | null;
    insurance_value_usd: number | null;
    open_issues_count: number;
    total_financial_impact_usd: number | null;
    blocking_issues_count: number;
    status: string;
    estimated_arrival_date: string | null;
    carrier_name: string | null;
}

export interface RiskyShipmentsResponse {
    containers: RiskyShipment[];
    total_count: number;
    skip: number;
    limit: number;
    has_more: boolean;
    as_of: string;
}

// ============================================================================
// QUERY KEY
// ============================================================================

export const RISKY_SHIPMENTS_QUERY_KEY = ["risky-shipments"] as const;

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to fetch risky shipments (high/critical risk sorted by value)
 *
 * @param startDate - Start of date range (ISO format: "2024-01-01") - filters by ETA/ETD
 * @param endDate - End of date range (ISO format: "2024-01-31") - filters by ETA/ETD
 * @param skip - Number of records to skip for pagination (default: 0)
 * @param limit - Number of records to return (default: 20)
 * @param includeMedium - Include medium risk containers (>= 0.25) in addition to high/critical (>= 0.50)
 * @param enabled - Whether the query should be enabled (default: true)
 *
 * Returns containers with:
 * - risk_score >= 0.50 (high or critical) by default
 * - risk_score >= 0.25 (medium, high, or critical) when includeMedium is true
 * - Sorted by total_value_usd DESC
 * - Includes open issues count and financial impact
 */
export interface UseRiskyShipmentsOptions {
    startDate: string | null;
    endDate: string | null;
    skip?: number;
    limit?: number;
    includeMedium?: boolean;
    carrierScac?: string;
    shipmentStatus?: ShipmentStatusFilter | "";
    enabled?: boolean;
}

export function useRiskyShipments(options: UseRiskyShipmentsOptions) {
    const {
        startDate,
        endDate,
        skip = 0,
        limit = 20,
        includeMedium = false,
        enabled = true,
        carrierScac,
        shipmentStatus,
    } = options;

    return useQuery({
        queryKey: [
            ...RISKY_SHIPMENTS_QUERY_KEY,
            { startDate, endDate, skip, limit, includeMedium, carrierScac, shipmentStatus },
        ],
        queryFn: async (): Promise<RiskyShipmentsResponse> => {
            return await apiGet<RiskyShipmentsResponse>("/containers-recap/risky", {
                params: {
                    ...(startDate && { start_date: startDate }),
                    ...(endDate && { end_date: endDate }),
                    skip,
                    limit,
                    ...(includeMedium && { include_medium: true }),
                    ...(carrierScac && { carrier_scac: carrierScac }),
                    ...(shipmentStatus && { shipment_status: shipmentStatus }),
                },
            });
        },
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchInterval: 10 * 60 * 1000, // Refresh every 10 minutes
    });
}
